import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/lib/ConfigProvider'
import { formatMoney, parseMinor } from '@/lib/money'
import { CURRENCY_SYMBOL } from '@/lib/currencyNames'
import { fetchAccounts } from '@/accounts/accounts'
import type { AccountsState } from '@/accounts/accounts'
import { fetchQuote } from '@/money/quote'
import type { QuoteState } from '@/money/quote'
import { fetchPaymentOptions, railDisplay } from '@/money/paymentOptions'
import type { PaymentOption, PaymentOptionsState } from '@/money/paymentOptions'
import './SendPanel.css'

/**
 * The design's Send money panel, with the parts that can be true (#159).
 *
 * It rendered `FIXTURE_QUOTE`: 1,200.00 EUR, "MIKA GETS 1,299.60 USD",
 * "Fee 3.84 · rate 1.0830", "Arrives in seconds". None of it moved and none of
 * it was anybody's. It also existed TWICE — Overview's rail and the Payments
 * page — as two copies of one markup, which is why this is a component.
 *
 * # What is real now
 *
 *   the amount     typed, parsed with `parseMinor`
 *   the currency   an account the customer actually holds
 *   the fee        `GET /v1/fees/quote`, live, with the schedule's own label
 *
 * # What is NOT here, and why that is the honest answer
 *
 * There is no exchange rate anywhere in the published contract — no FX route,
 * no rate on any schema; the only `rate_bps` fields are VAT. So the second leg
 * and "rate 1.0830" cannot be made true, and a screen that keeps showing them
 * beside three figures that ARE true is more misleading than one where
 * everything was invented. They are gone rather than replaced by a second
 * fabrication.
 *
 * # Quoting is not instructing
 *
 * The route is a GET because "the one thing this route must never become is a
 * way to charge somebody". Continue still goes to Payments: a cash-out needs a
 * strong-authentication challenge bound to the exact amount and destination and
 * an idempotency key that is one intent (invariant 5), and none of that is
 * built.
 */
export function SendPanel({ heading = 'Send money' }: { heading?: string }) {
  const api = useApi()
  const [accounts, setAccounts] = useState<AccountsState>({ status: 'loading' })
  const [currency, setCurrency] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const [quote, setQuote] = useState<QuoteState>({ status: 'idle' })
  /**
   * Where the money is going. NOT DEFAULTED, and that is the point.
   *
   * The panel used to ask `outgoing_payment` with no method, which the fee
   * route refuses with `destination_needed` — "Choose where the money is going
   * and we will show the fee." So the screen printed an instruction with no
   * control to follow it, on every single load.
   *
   * Defaulting to SEPA would have made the fee line work and would have shown
   * somebody a price for a rail they never chose. An unset value asks for
   * nothing and says so.
   */
  const [destinationID, setDestinationID] = useState('')
  /**
   * Which rails can actually carry this, from the ROUTING ENGINE.
   *
   * The picker was a hand-written table of four rails and four sentences about
   * how long each takes. Three of the four are not priced on the customer's
   * schedule, so three of four choices led to "your fee list does not price
   * this" — and the arrival times were guesses. The contract says why a client
   * must not do that: two sources of truth about availability "disagree on
   * exactly the days it matters".
   */
  const [rails, setRails] = useState<PaymentOptionsState>({ status: 'idle' })

  useEffect(() => {
    let live = true
    void fetchAccounts(api).then((a) => {
      if (!live) return
      setAccounts(a)
      // The FIRST account, not a hardcoded 'EUR'. Invariant 4: a currency is
      // never defaulted, and the customer's own account is the only thing here
      // entitled to choose one.
      if (a.status === 'ready' && a.accounts[0]) setCurrency(a.accounts[0].currency)
    })
    return () => {
      live = false
    }
  }, [api])

  /**
   * What the customer typed, as minor units — or why it is not a number yet.
   *
   * `parseMinor`, never `parseFloat`: `parseFloat('0.29') * 100` is
   * 28.999999999999996 (CLAUDE.md §3). It also scales off the ISO-4217
   * exponent, so this is right for JPY's zero digits and BHD's three without
   * knowing which currency it was handed.
   */
  const minor = ((): bigint | null => {
    if (currency === null || typed.trim() === '') return null
    try {
      return parseMinor(typed, currency)
    } catch {
      return null
    }
  })()

  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const generation = useRef(0)

  const ask = useCallback(
    (cur: string, amountMinor: bigint | null, destID: string) => {
      const destination = destID === '' ? undefined : railDisplay(destID)
      if (destination === undefined) {
        // NOT A REQUEST. The route would answer `destination_needed`, and
        // rendering that as "we could not check the fee" turns a question we
        // have not asked the customer yet into a failure.
        setQuote({ status: 'idle' })
        return
      }
      const mine = ++generation.current
      setQuote({ status: 'loading' })
      void fetchQuote(api, {
        kind: destination.kind,
        currency: cur,
        // The pairing comes from the table, not from here: `internal_transfer`
        // is not method-keyed and `outgoing_payment` is, and getting it the
        // wrong way round is a 400 in both directions.
        ...(destination.method === undefined ? {} : { method: destination.method }),
        // ABSENT rather than zero when nothing is typed: the contract calls an
        // absent base "a different fact from a base of zero".
        ...(amountMinor === null ? {} : { amountMinor }),
      }).then((q) => {
        // Last write wins by generation, not by arrival: a slow quote for an
        // old amount must never overwrite a fresh one, or the customer reads a
        // fee for a number they have already changed.
        if (mine === generation.current) setQuote(q)
      })
    },
    [api],
  )

  useEffect(() => {
    if (currency === null) return
    // Debounced: a quote per keystroke is a request per keystroke, and the
    // customer is still typing the number it would price.
    clearTimeout(timer.current)
    timer.current = setTimeout(() => ask(currency, minor, destinationID), 300)
    return () => clearTimeout(timer.current)
  }, [ask, currency, minor, destinationID])

  // The rails, asked at the same amount the quote is asked at: availability is
  // amount-dependent — a rail with a ceiling drops out above it — so a list
  // fetched once at load would offer a destination the seam then refuses.
  const railGeneration = useRef(0)
  useEffect(() => {
    if (currency === null) return
    const mine = ++railGeneration.current
    setRails({ status: 'loading' })
    const t = setTimeout(() => {
      void fetchPaymentOptions(api, currency, minor).then((r) => {
        // Last write wins by generation, not arrival, for the quote's reason.
        if (mine === railGeneration.current) setRails(r)
      })
    }, 300)
    return () => clearTimeout(t)
  }, [api, currency, minor])

  // A destination that stops being offered must stop being selected, or the
  // fee line prices a rail the picker no longer shows.
  useEffect(() => {
    if (rails.status !== 'ready' || destinationID === '') return
    if (!rails.options.some((o) => o.method === destinationID && o.available)) {
      setDestinationID('')
    }
  }, [rails, destinationID])

  const held = accounts.status === 'ready' ? accounts.accounts : []
  // Written as a conditional rather than `CURRENCY_SYMBOL[c] ?? c`, which
  // `check-invariants.sh` reads — correctly, as a pattern — as a defaulted
  // currency. Rephrasing beats exempting: an exemption outlives its reason.
  /*
    The symbol, or NOTHING.
    
    Written as a conditional rather than `CURRENCY_SYMBOL[c] ?? c`, which
    `check-invariants.sh` reads — correctly, as a pattern — as a defaulted
    currency. Rephrasing beats exempting: an exemption outlives its reason.
    
    And falling back to the CODE was wrong on its own terms: the code is already
    rendered beside the disc, so a currency this bundle has no symbol for drew
    "JPY" twice. An empty disc says the same thing once.
  */
  const symbol = currency === null ? '' : (CURRENCY_SYMBOL[currency] ?? '')

  // The chosen destination's own sentence, or nothing. Rendered under the
  // picker so a rail is not a word somebody has to already know: "SEPA Instant"
  // means nothing to most people and "usually within seconds" does.
  // The chosen rail's own arrival estimate, from the routing engine. Never a
  // sentence written here about how long a rail takes.
  const chosenOption =
    rails.status === 'ready' ? rails.options.find((o) => o.method === destinationID) : undefined
  const chosenHint =
    chosenOption?.valueDate === undefined ? '' : `Arrives ${chosenOption.valueDate}.`

  return (
    <section className="send" aria-labelledby="send-heading">
      <h2 id="send-heading" className="ov__sectionTitle">
        {heading}
      </h2>

      <div className="send__pair">
        <div className="send__leg send__leg--top send__leg--input">
          <label className="eyebrow" htmlFor="send-amount">
            You send
          </label>
          <input
            id="send-amount"
            className="send__amount send__input tnum"
            inputMode="decimal"
            autoComplete="off"
            // `formatMoney` of a zero, never `toFixed`: the placeholder must
            // show the right number of minor digits, and `toFixed` is the exact
            // call CLAUDE.md §3 forbids for exactly that reason.
            placeholder={currency === null ? '' : formatMoney({ amountMinor: 0n, currency })}
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            disabled={currency === null}
          />
          <span className="send__currency">
            {symbol === '' ? null : (
              <span className="send__coin send__coin--accent" aria-hidden="true">
                {symbol}
              </span>
            )}
            {held.length > 1 ? (
              <select
                className="send__pick"
                aria-label="Currency"
                value={currency === null ? '' : currency}
                onChange={(e) => setCurrency(e.currentTarget.value)}
              >
                {held.map((a) => (
                  <option key={a.accountRef} value={a.currency}>
                    {a.currency}
                  </option>
                ))}
              </select>
            ) : (
              <span>{currency === null ? '' : currency}</span>
            )}
          </span>
        </div>
      </div>

      {/*
        THE CONTROL THE FEE LINE WAS ALREADY ASKING FOR.

        `GET /v1/fees/quote` refuses `outgoing_payment` with no method —
        "Choose where the money is going and we will show the fee" — and this
        panel had no way to choose, so it printed that sentence to everybody on
        every load.
      */}
      <div className="send__leg send__leg--dest">
        <label className="eyebrow" htmlFor="send-destination">
          Where is it going
        </label>
        <select
          id="send-destination"
          className="send__dest"
          value={destinationID}
          disabled={rails.status !== 'ready'}
          onChange={(e) => setDestinationID(e.currentTarget.value)}
        >
          {/* An empty first option, not a preselected rail. Showing a price for
              a destination nobody chose is worse than showing none. */}
          <option value="">
            {rails.status === 'idle'
              ? // BEFORE AN AMOUNT there is nothing to ask the router about, and
                // saying "we could not read where you can send" for a question
                // nobody has asked is a failure message on a working screen.
                'Enter an amount first'
              : rails.status === 'loading'
                ? 'Reading where you can send…'
                : rails.status === 'unavailable'
                  ? 'We could not read where you can send'
                  : 'Choose a destination'}
          </option>
          {rails.status === 'ready'
            ? rails.options.map((o) => (
                // UNAVAILABLE RAILS ARE SHOWN, DISABLED, rather than hidden. A
                // choice that silently disappears is one somebody looks for and
                // cannot find, and "why can I not send to X" has an answer.
                <option key={o.method} value={o.method} disabled={!o.available}>
                  {railDisplay(o.method).label}
                  {o.available ? '' : ' — not available'}
                </option>
              ))
            : null}
        </select>
        {chosenHint === '' ? null : <span className="send__destHint">{chosenHint}</span>}
        {rails.status === 'ready' ? <UnavailableRails options={rails.options} /> : null}
        {rails.status === 'unavailable' ? (
          // NOT "nothing is available". We could not ask, and saying otherwise
          // tells somebody they cannot send when they may well be able to.
          <span className="send__destHint">{rails.reason}</span>
        ) : null}
      </div>

      <div className="send__meta">
        <span className="send__fee">{feeLine(quote, typed, destinationID)}</span>
      </div>

      {/*
        A LINK, NOT A SUBMIT. Quoting is not instructing: a cash-out needs a
        challenge bound to the exact amount and destination and an idempotency
        key that is one intent (invariant 5). Payments says so where a customer
        can read it.
      */}
      <Link to="/payments" className="send__continue">
        Continue
      </Link>

      {accounts.status === 'none' || accounts.status === 'unregistered' ? (
        <p className="send__sample">You do not hold an account to send from yet.</p>
      ) : accounts.status === 'unavailable' ? (
        <p className="send__sample">We could not read your accounts, so there is nothing to quote against.</p>
      ) : null}
    </section>
  )
}

/**
 * Why a rail is not on offer, in the backend's own words.
 *
 * Rendered rather than hidden, and rendered VERBATIM: the routing engine writes
 * the sentence and it changes without this bundle changing. A friendlier one
 * invented here would be less true, and a rail that vanishes with no explanation
 * is the question "why can I not send to X" with nowhere to look.
 */
function UnavailableRails({ options }: { options: readonly PaymentOption[] }) {
  const blocked = options.filter((o) => !o.available && o.message !== undefined)
  if (blocked.length === 0) return null
  return (
    <ul className="send__blocked">
      {blocked.map((o) => (
        <li key={o.method}>
          <strong>{railDisplay(o.method).label}</strong> — {o.message}
        </li>
      ))}
    </ul>
  )
}

/**
 * What to say about the fee.
 *
 * `waived` is rendered as waived rather than as `0.00`: "no fee is charged for
 * you" and "the fee for this is nothing" are different statements, and only one
 * of them changes when a list is republished.
 */
function feeLine(quote: QuoteState, typed: string, destinationID: string): string {
  switch (quote.status) {
    case 'idle':
      // THREE IDLES, and they ask for three different things. Sending somebody
      // to the wrong control is the defect this replaced: the panel told
      // everybody to choose a destination when the destination list could not
      // even be fetched until an amount existed.
      //
      // The amount comes FIRST because availability is priced at one — a rail
      // with a ceiling is offered below it and withheld above — so the router
      // cannot answer "where can this go" until there is a "this".
      if (typed.trim() === '') return 'Type an amount, then choose where it is going.'
      return destinationID === ''
        ? 'Choose where the money is going and we will show the fee.'
        : 'Working out the fee…'
    case 'loading':
      return 'Checking the fee…'
    case 'ready':
      return quote.quote.waived
        ? `${quote.quote.label} — waived`
        : `${quote.quote.label} ${formatMoney(quote.quote.fee, { withCurrency: true })}`
    case 'not_priced':
      // An ANSWER, not a fault, and never substituted with another currency's
      // price or another rail's.
      return typed.trim() === ''
        ? 'This is not priced on your fee list.'
        : 'Your fee list does not price this. We will not guess at another one.'
    case 'unavailable':
      return `We could not check the fee. ${quote.reason}`
  }
}
